import { ArrowLeftRight, History, RotateCcw, Save, SaveAll, Search, Share2, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { authFetch, useAuth } from '../auth'
import { loadCalculatorBootstrap, peekCalculatorBootstrap } from '../calculatorBootstrapCache'
import { partitionCalculatorRecords, type CalculatorRecordScope } from '../calculatorHistory'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { CalculatorShareDialog } from '../components/CalculatorShareDialog'
import {
  type PinduoduoProductCatalog,
  type PinduoduoMasterVariant,
} from '../platform'
import { pinduoduoIsPaidOrderOutcome, type PinduoduoOrderFacts } from '../pinduoduo'
import { pddPaidOrderCoverageIssue, pddPaymentCohortIssue, type PinduoduoAfterSalesFacts } from '../pinduoduoAfterSales'
import { internalProductCost } from '../productMaster'
import {
  afterSalesDefaultsFor,
  calculatorDefaultsForVariant,
  computeAfterSalesAdjusted,
  computePinduoduoCashRecovery,
  computePinduoduoUnitProfit,
  simulateAdvertisingAfterAfterSales,
  type AfterSalesInputs,
  type CalculatorDefaults,
  type CalculatorInputs,
} from '../pinduoduoProfitCalculator'
import { money } from '../utils'
import type { CompanyProductOverrides, ErpInventory, InternalProductInfo, StoreCostProfiles } from '../types'

const PDD_STORE_ID = 'pdd-beijuxiong-youhe'
const BOOTSTRAP_URL = '/api/calculators/pinduoduo/bootstrap'
const RECORDS_URL = '/api/calculators/pinduoduo/records'
const RECORDS_API = RECORDS_URL

interface CalculatorRecord {
  id: string
  code?: string
  savedAt: string
  store?: string
  variantId: string
  productId: string
  productName: string
  variantAttributes: string
  merchantVariantCode: string
  previewImage?: string
  note: string
  inputs: CalculatorInputs
  afterSales?: AfterSalesInputs
  adSpendText?: string
  createdBy?: string
  createdByName?: string
  isMine?: boolean
  accessLevel?: 'owner' | 'read' | 'edit'
  canEdit?: boolean
  canShare?: boolean
  canDelete?: boolean
  canCopy?: boolean
  revision?: number
  lastEditedBy?: string
  lastEditedByName?: string
  updatedAt?: number
  shareCount?: number
}

interface PinduoduoCalculatorBootstrap {
  catalog: PinduoduoProductCatalog
  internalProductInfo: InternalProductInfo
  companyProductOverrides: CompanyProductOverrides
  storeCostProfiles: StoreCostProfiles
  erpInventory: ErpInventory
  orderFacts: Pick<PinduoduoOrderFacts, 'meta' | 'dailySku'>
  afterSalesFacts: Pick<PinduoduoAfterSalesFacts, 'meta' | 'paymentCohortProducts'>
  records: CalculatorRecord[]
}

const savedAtText = (value: string) => value.replace('T', ' ').slice(0, 16)

const fulfillmentFields: Array<{ key: keyof CalculatorInputs; label: string; unit: string }> = [
  { key: 'productCost', label: '产品成本', unit: '元' },
  { key: 'packagingCost', label: '包装费', unit: '元' },
  { key: 'giftCost', label: '赠品成本', unit: '元' },
  { key: 'shippingInsuranceCost', label: '运费险', unit: '元' },
  { key: 'shippingCost', label: '运费', unit: '元' },
]

const rateFields: Array<{ key: keyof CalculatorInputs; label: string; unit: string; hint?: string }> = [
  { key: 'platformFeeRatePercent', label: '平台扣点', unit: '%' },
  { key: 'taxRatePercent', label: '税点', unit: '%' },
  { key: 'rebateRatePercent', label: '返点率', unit: '%', hint: '按产品成本计算返点' },
]

const inputFieldKeys: Array<keyof CalculatorInputs> = [
  'price', 'productCost', 'packagingCost', 'giftCost', 'shippingInsuranceCost', 'shippingCost',
  'platformFeeRatePercent', 'taxRatePercent', 'rebateRatePercent',
]

const percent = (value: number | null) => value === null ? '—' : `${(value * 100).toFixed(1)}%`
const roi = (value: number | null) => value === null ? '—' : value.toFixed(2)

const afterSalesFields: Array<{ key: keyof AfterSalesInputs; label: string; unit: string; hint?: string }> = [
  { key: 'secondReturnRatePercent', label: '预计二退率', unit: '%', hint: '二退件数占下单件数' },
  { key: 'returnShippingCost', label: '往返运费', unit: '元', hint: '默认发货运费×2' },
  { key: 'packagingLossCost', label: '包装损耗', unit: '元' },
  { key: 'damageRatePercent', label: '货损率', unit: '%', hint: '按产品成本计预期损坏' },
]


const companyFactsFor = (
  records: Array<{ generalAgentPrice: number | null; provincialAgentPrice: number | null; ecommercePrice: number | null; brandSpecialInitialPrice: number | null; specialPrice0225: number | null; specialPrice0305: number | null }>,
  erpAvailable: number | null | undefined,
) => {
  const firstPositive = (values: Array<number | null>) =>
    values.find((value) => typeof value === 'number' && value > 0) ?? null
  const resolution = internalProductCost(records as never)
  return {
    generalAgentPrice: resolution.generalAgentPrice ?? firstPositive(records.map((r) => r.generalAgentPrice)),
    brandSpecialPrice: resolution.brandSpecialPrice,
    brandSpecialConflict: resolution.issue === 'special-price-conflict',
    provincialAgentPrice: firstPositive(records.map((r) => r.provincialAgentPrice)),
    ecommercePrice: firstPositive(records.map((r) => r.ecommercePrice)),
    erpAvailable: typeof erpAvailable === 'number' ? erpAvailable : null,
    hasCompanyRecord: records.length > 0,
  }
}

export function PinduoduoProfitCalculatorPage() {
  const auth = useAuth()
  const userId = auth.user?.id || ''
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedVariantId = searchParams.get('sku')?.trim() || ''
  const requestedRecordId = searchParams.get('record')?.trim() || ''
  const autoSelectedVariantId = useRef('')
  const pendingSkuParam = useRef('')
  const [bootstrap, setBootstrap] = useState<PinduoduoCalculatorBootstrap | null>(() => peekCalculatorBootstrap(userId, 'pdd'))
  const [dataError, setDataError] = useState('')
  const resource = <T,>(data: T | null) => ({ data, loading: !bootstrap && !dataError, error: dataError })
  const catalog = resource(bootstrap?.catalog ?? null)
  const internalProducts = resource(bootstrap?.internalProductInfo ?? null)
  const companyOverrides = resource(bootstrap?.companyProductOverrides ?? null)
  const storeCostProfiles = resource(bootstrap?.storeCostProfiles ?? null)
  const orderFacts = resource(bootstrap?.orderFacts ?? null)
  const erpInventory = resource(bootstrap?.erpInventory ?? null)
  const afterSalesFacts = resource(bootstrap?.afterSalesFacts ?? null)

  const [query, setQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const searchPopRef = useRef<HTMLDivElement | null>(null)
  const [selected, setSelected] = useState<PinduoduoMasterVariant | null>(null)
  const [defaults, setDefaults] = useState<CalculatorDefaults | null>(null)
  const [inputs, setInputs] = useState<CalculatorInputs | null>(null)
  const [afterSalesInputs, setAfterSalesInputs] = useState<AfterSalesInputs | null>(null)
  const [adSpendText, setAdSpendText] = useState('')
  const [records, setRecords] = useState<CalculatorRecord[]>([])
  const [recordsError, setRecordsError] = useState('')
  const [recordsBusy, setRecordsBusy] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [loadedRecordId, setLoadedRecordId] = useState<string | null>(null)
  const [copiedCode, setCopiedCode] = useState('')
  const [recordNoteText, setRecordNoteText] = useState('')
  const [recordScope, setRecordScope] = useState<CalculatorRecordScope>('mine')
  const [shareRecord, setShareRecord] = useState<CalculatorRecord | null>(null)
  const [conflictRevision, setConflictRevision] = useState<number | null>(null)

  const copyRecordCode = (code: string) => {
    void navigator.clipboard?.writeText(code).catch(() => {})
    setCopiedCode(code)
    window.setTimeout(() => setCopiedCode((current) => current === code ? '' : current), 1500)
  }

  const refreshRecords = async () => {
    const response = await fetch(RECORDS_URL, { cache: 'no-store' })
    const payload = await response.json() as { error?: string; records?: CalculatorRecord[] }
    if (!response.ok) throw new Error(payload.error || `历史测算加载失败：${response.status}`)
    const next = payload.records || []
    setRecords(next)
    return next
  }

  useEffect(() => {
    let active = true
    loadCalculatorBootstrap<PinduoduoCalculatorBootstrap>(userId, 'pdd', BOOTSTRAP_URL)
      .then((payload) => { if (active) { setBootstrap(payload); setRecords(payload.records || []) } })
      .catch((reason) => { if (active) setDataError(reason instanceof Error ? reason.message : String(reason)) })
    refreshRecords()
      .then(() => undefined)
      .catch((reason) => { if (active) setRecordsError(reason instanceof Error ? reason.message : String(reason)) })
    return () => { active = false }
  }, [userId])

  useEffect(() => {
    if (!searchOpen) return
    const onPointerDown = (event: MouseEvent) => {
      if (searchPopRef.current && !searchPopRef.current.contains(event.target as Node)) setSearchOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [searchOpen])

  // 提交前先拉取服务端最新记录并在其上合并本次改动，避免陈旧标签页整体覆盖丢数据
  const persistRecords = async (op: { type: 'upsert'; record: CalculatorRecord } | { type: 'delete'; id: string }) => {
    setRecordsBusy(true)
    setRecordsError('')
    try {
      let base = records.filter((record) => record.isMine)
      try {
        const fresh = await fetch(RECORDS_URL, { cache: 'no-store' })
        if (fresh.ok) base = (((await fresh.json()) as { records?: CalculatorRecord[] }).records || [])
          .filter((record) => record.isMine)
      } catch { /* 拉取失败时退回本地状态 */ }
      const next = op.type === 'delete'
        ? base.filter((record) => record.id !== op.id)
        : base.some((record) => record.id === op.record.id)
          ? base.map((record) => record.id === op.record.id ? op.record : record)
          : [op.record, ...base].slice(0, 200)
      const response = await authFetch(RECORDS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ records: next }),
      })
      const payload = await response.json() as { error?: string; code?: string; currentRevision?: number; records?: CalculatorRecord[] }
      if (!response.ok) {
        if (response.status === 409 && payload.code === 'calculator_conflict') setConflictRevision(payload.currentRevision || null)
        throw new Error(payload.error || '测算记录保存失败')
      }
      setConflictRevision(null)
      setRecords(payload.records || next)
      setRecordScope('mine')
      return true
    } catch (reason) {
      setRecordsError(reason instanceof Error ? reason.message : String(reason))
      return false
    } finally {
      setRecordsBusy(false)
    }
  }

  const updateSharedRecord = async (record: CalculatorRecord, expectedRevision: number) => {
    setRecordsBusy(true)
    setRecordsError('')
    try {
      const response = await authFetch(`${RECORDS_API}/update`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ record, expectedRevision }),
      })
      const payload = await response.json() as { error?: string; code?: string; currentRevision?: number; record?: CalculatorRecord }
      if (!response.ok || !payload.record) {
        if (response.status === 409 && payload.code === 'calculator_conflict') setConflictRevision(payload.currentRevision || null)
        throw new Error(payload.error || '共享测算保存失败')
      }
      setConflictRevision(null)
      setRecords((current) => current.map((item) => item.id === payload.record!.id ? payload.record! : item))
      return true
    } catch (reason) {
      setRecordsError(reason instanceof Error ? reason.message : String(reason))
      return false
    } finally {
      setRecordsBusy(false)
    }
  }

  const saveCurrent = async (mode: 'overwrite' | 'new') => {
    if (!selected || !inputs) return
    setSaveMessage('')
    const existing = mode === 'overwrite' && loadedRecordId
      ? records.find((record) => record.id === loadedRecordId && record.canEdit)
      : undefined
    const record: CalculatorRecord = {
      id: existing?.id ?? `calc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      ...(existing?.code ? { code: existing.code } : {}),
      ...(existing?.revision ? { revision: existing.revision } : {}),
      savedAt: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 19),
      store: PDD_STORE_ID,
      variantId: selected.variantId,
      productId: selected.productId,
      productName: selected.productName,
      variantAttributes: selected.variantAttributes,
      merchantVariantCode: selected.merchantVariantCode,
      previewImage: selected.previewImage,
      note: recordNoteText.trim(),
      inputs: { ...inputs },
      ...(afterSalesInputs ? { afterSales: { ...afterSalesInputs } } : {}),
      adSpendText,
    }
    const saved = existing && !existing.isMine
      ? await updateSharedRecord(record, existing.revision || 0)
      : await persistRecords({ type: 'upsert', record })
    if (saved) {
      setLoadedRecordId(record.id)
      const title = record.note || `${selected.productName} · ${selected.variantAttributes || '默认规格'}`
      setSaveMessage(existing
        ? existing.isMine
          ? `已覆盖保存测算记录「${title}」。`
          : `已保存对共享测算「${title}」的修改。`
        : mode === 'new' && loadedRecordId
          ? `已另存为新的测算记录「${title}」。`
          : `已保存测算记录「${title}」。`)
    }
  }

  const deleteRecord = (id: string) => {
    if (id === loadedRecordId) setLoadedRecordId(null)
    void persistRecords({ type: 'delete', id })
  }

  // 恢复记录保存时的完整参数快照；旧记录缺少快照字段时退回默认值
  const applyRecordSnapshot = (record: CalculatorRecord) => {
    setInputs({ ...record.inputs })
    setAfterSalesInputs((previous) => {
      const restoredAfterSales = record.afterSales
        ? { ...record.afterSales }
        : afterSalesDefaultsFor(record.inputs, defaultSecondReturnRate(record.productId) ?? previous?.secondReturnRatePercent ?? 0)
      return {
        ...restoredAfterSales,
        secondReturnRatePercent: Math.min(100, Math.max(0, restoredAfterSales.secondReturnRatePercent)),
      }
    })
    setAdSpendText(record.adSpendText ?? '')
    setRecordNoteText(record.note ?? '')
    setLoadedRecordId(record.id)
  }

  const loadRecord = (record: CalculatorRecord) => {
    setSaveMessage('')
    setConflictRevision(null)
    const variant = catalog.data?.variants.find((item) => item.variantId === record.variantId)
    if (variant) {
      selectVariant(variant, { keepRecord: true })
      applyRecordSnapshot(record)
      return
    }
    const synthetic: PinduoduoMasterVariant = {
      variantId: record.variantId,
      productId: record.productId,
      productName: record.productName,
      merchantVariantCode: record.merchantVariantCode,
      merchantProductCode: '',
      variantAttributes: record.variantAttributes,
      color: '',
      size: '',
      stock: 0,
      groupPrice: record.inputs.price,
      singlePrice: null,
      referencePrice: null,
      productStatus: '已不在主档',
      variantStatus: '已不在主档',
      previewImage: record.previewImage || '',
      mappingStatus: 'unmapped',
      companyVariantId: null,
    }
    setSelected(synthetic)
    setDefaults({
      inputs: { ...record.inputs },
      isAccessory: false,
      isBrandSpecial: false,
      hasProductCost: true,
      hasStoreCost: true,
      costIssue: null,
      storeCostEffectiveDate: '',
    })
    applyRecordSnapshot(record)
    setQuery('')
    syncSkuParam(record.variantId, true)
  }

  useEffect(() => {
    if (!requestedRecordId || loadedRecordId === requestedRecordId) return
    if (!catalog.data) return
    const record = records.find((item) => item.id === requestedRecordId)
    if (record) loadRecord(record)
  }, [catalog.data, records, requestedRecordId, loadedRecordId])

  const loading = catalog.loading || internalProducts.loading || companyOverrides.loading || storeCostProfiles.loading
  const error = catalog.error || internalProducts.error || companyOverrides.error || storeCostProfiles.error

  const companyRecordsByCode = useMemo(() => {
    const map = new Map<string, NonNullable<typeof internalProducts.data>['records']>()
    for (const record of internalProducts.data?.records || []) {
      const code = record.productCode.trim()
      if (!code) continue
      const records = map.get(code) || []
      records.push(record)
      map.set(code, records)
    }
    return map
  }, [internalProducts.data])

  const overridesByCode = useMemo(() => new Map(
    (companyOverrides.data?.records || []).map((record) => [record.companyVariantId.trim(), record]),
  ), [companyOverrides.data])

  const erpByCode = useMemo(() => new Map(
    (erpInventory.data?.records || []).map((record) => [String(record.productCode).trim(), record.available]),
  ), [erpInventory.data])

  const profile = useMemo(() => storeCostProfiles.data?.stores.find((item) =>
    item.platformId === 'pinduoduo' && item.storeId === PDD_STORE_ID,
  ), [storeCostProfiles.data])

  // 支付队列二退率参考：二退件数（按原支付日期归队）÷ 下单件数（确认成交+退款成功+售后处理中）。
  const secondReturnReference = useMemo(() => {
    const issue = pddPaymentCohortIssue(afterSalesFacts.data?.meta.paymentCohort, orderFacts.data?.meta.sourceSha256)
      || pddPaidOrderCoverageIssue(orderFacts.data?.meta, orderFacts.data?.meta.queryStart || '', orderFacts.data?.meta.queryEnd || '')
    const orderedByProduct = new Map<string, number>()
    let orderedTotal = 0
    for (const row of orderFacts.data?.dailySku || []) {
      if (!pinduoduoIsPaidOrderOutcome(row.outcome)) continue
      orderedByProduct.set(row.productId, (orderedByProduct.get(row.productId) || 0) + row.soldItemCount)
      orderedTotal += row.soldItemCount
    }
    const secondByProduct = new Map<string, number>()
    let secondTotal = 0
    for (const row of afterSalesFacts.data?.paymentCohortProducts || []) {
      secondByProduct.set(row.productId, (secondByProduct.get(row.productId) || 0) + row.secondReturnItemCount)
      secondTotal += row.secondReturnItemCount
    }
    return {
      issue,
      storeRatePercent: !issue && orderedTotal > 0 ? secondTotal / orderedTotal * 100 : null,
      productRatePercent: (productId: string) => {
        const ordered = orderedByProduct.get(productId) || 0
        if (issue || ordered < 20) return null
        return (secondByProduct.get(productId) || 0) / ordered * 100
      },
      productOrderedItems: (productId: string) => orderedByProduct.get(productId) || 0,
    }
  }, [afterSalesFacts.data, orderFacts.data])

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    if (!keyword || !catalog.data) return []
    return catalog.data.variants.filter((variant) =>
      variant.productName.toLowerCase().includes(keyword)
      || variant.variantAttributes.toLowerCase().includes(keyword)
      || variant.variantId.includes(keyword)
      || variant.merchantVariantCode.toLowerCase().includes(keyword)
      || variant.productId.includes(keyword),
    ).slice(0, 30)
  }, [catalog.data, query])

  const defaultSecondReturnRate = (productId: string) => {
    const rate = secondReturnReference.productRatePercent(productId) ?? secondReturnReference.storeRatePercent
    return rate === null ? null : Math.round(rate * 10) / 10
  }

  // 选中后把 ?sku= 写入 URL，让详情/列表切换由 URL 驱动；导航更新在 startTransition 中晚于本地 state 生效，需用 pendingSkuParam 防止清空 effect 误判
  const syncSkuParam = (variantId: string, keepRecord = false) => {
    autoSelectedVariantId.current = variantId
    pendingSkuParam.current = variantId
    if (requestedVariantId !== variantId || (!keepRecord && requestedRecordId)) {
      setSearchParams((params) => {
        const next = new URLSearchParams(params)
        next.set('sku', variantId)
        if (!keepRecord) next.delete('record')
        return next
      })
    }
  }

  const selectVariant = (variant: PinduoduoMasterVariant, options?: { keepRecord?: boolean }) => {
    const today = new Date().toISOString().slice(0, 10)
    const nextDefaults = calculatorDefaultsForVariant(
      variant,
      profile,
      companyRecordsByCode.get(variant.merchantVariantCode.trim()) || [],
      overridesByCode.get(variant.merchantVariantCode.trim()),
      today,
    )
    setSelected(variant)
    setDefaults(nextDefaults)
    setInputs({ ...nextDefaults.inputs })
    setAfterSalesInputs((previous) => afterSalesDefaultsFor(
      nextDefaults.inputs,
      defaultSecondReturnRate(variant.productId) ?? previous?.secondReturnRatePercent ?? 0,
    ))
    setAdSpendText('')
    setQuery('')
    setSearchOpen(false)
    setLoadedRecordId(null)
    setSaveMessage('')
    setRecordNoteText('')
    syncSkuParam(variant.variantId, options?.keepRecord)
  }

  useEffect(() => {
    if (loading || !requestedVariantId || autoSelectedVariantId.current === requestedVariantId || !catalog.data) return
    const variant = catalog.data.variants.find((item) => item.variantId === requestedVariantId)
    if (!variant) return
    autoSelectedVariantId.current = requestedVariantId
    selectVariant(variant)
  }, [catalog.data, loading, requestedVariantId])

  // 点击左侧菜单回到无 ?sku= 的同路由时，清空选中状态恢复默认列表视图
  useEffect(() => {
    if (requestedVariantId === pendingSkuParam.current) pendingSkuParam.current = ''
    if (requestedVariantId || !selected) return
    if (pendingSkuParam.current) return
    autoSelectedVariantId.current = ''
    setSelected(null)
    setDefaults(null)
    setInputs(null)
    setAfterSalesInputs(null)
    setAdSpendText('')
    setQuery('')
    setSearchOpen(false)
    setLoadedRecordId(null)
    setSaveMessage('')
    setRecordNoteText('')
  }, [requestedVariantId, selected])

  const updateInput = (key: keyof CalculatorInputs, raw: string) => {
    if (!inputs) return
    const value = raw === '' ? 0 : Number(raw)
    if (!Number.isFinite(value) || value < 0) return
    setInputs({ ...inputs, [key]: value })
  }

  const { mine: ownRecords, shared: sharedRecords } = useMemo(
    () => partitionCalculatorRecords(records.filter((record) => (record.store || PDD_STORE_ID) === PDD_STORE_ID)),
    [records],
  )
  const visibleRecords = recordScope === 'mine' ? ownRecords : sharedRecords
  const codeQuery = query.trim().toUpperCase()
  const noteQuery = query.trim().toLowerCase()
  const recordMatches = noteQuery
    ? visibleRecords.filter((record) => (
      (record.code || '').toUpperCase() === codeQuery
      || (record.note || '').toLowerCase().includes(noteQuery)
    )).slice(0, 20)
    : []
  const result = inputs ? computePinduoduoUnitProfit(inputs) : null
  const afterSalesResult = result && inputs && afterSalesInputs
    ? computeAfterSalesAdjusted(result, inputs, afterSalesInputs)
    : null
  const updateAfterSalesInput = (key: keyof AfterSalesInputs, raw: string) => {
    if (!afterSalesInputs) return
    const value = raw === '' ? 0 : Number(raw)
    if (!Number.isFinite(value) || value < 0 || (key === 'secondReturnRatePercent' && value > 100)) return
    setAfterSalesInputs({ ...afterSalesInputs, [key]: value })
  }
  const adSpend = Number(adSpendText)
  const adSpendForRecovery = adSpendText === '' ? 0 : adSpend
  const adSimulation = afterSalesResult && adSpendText !== '' && Number.isFinite(adSpend) && adSpend >= 0
    ? simulateAdvertisingAfterAfterSales(afterSalesResult, adSpend)
    : null
  const edited = defaults && inputs
    ? inputFieldKeys.some((key) => inputs[key] !== defaults.inputs[key])
    : false
  const loadedRecord = loadedRecordId === null ? undefined : records.find((record) => record.id === loadedRecordId)
  const hasLoadedRecord = loadedRecord !== undefined
  const hasLoadedOwnRecord = loadedRecord?.isMine === true
  const hasLoadedEditableRecord = loadedRecord?.canEdit === true

  if (loading) return <div className="loading-state">正在加载利润计算器数据…</div>
  if (error) return <div className="error-state"><h1>利润计算器不可用</h1><p>{error}</p></div>

  const facts = selected
    ? companyFactsFor(companyRecordsByCode.get(selected.merchantVariantCode.trim()) || [], erpByCode.get(selected.merchantVariantCode.trim()))
    : null
  const inventoryQuantity = typeof facts?.erpAvailable === 'number' && facts.erpAvailable >= 0
    ? facts.erpAvailable
    : null
  const cashRecovery = result && inputs && afterSalesResult && afterSalesInputs
    ? computePinduoduoCashRecovery(
      result,
      inputs,
      afterSalesResult,
      afterSalesInputs,
      adSpendForRecovery,
      inventoryQuantity,
    )
    : null
  const hasCompleteRecoveryCosts = Boolean(defaults?.hasStoreCost)

  const searchPanel = (
    <>
      <div className="jdc-search">
        <Search aria-hidden />
        <input
          type="text"
          value={query}
          placeholder="搜索商品名称 / 规格 / 拼多多SKUID / SKU编码 / 商品ID，或输入计算ID（如 PDD-1）…"
          autoFocus={searchOpen}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {query.trim() && (
        <div className="jdc-results">
          {recordMatches.map((record) => (
            <button type="button" key={`record-${record.id}`} className="jdc-result" onClick={() => loadRecord(record)}>
              <span className="jdc-result-code">{record.code}</span>
              <span className="jdc-result-name">
                <strong>{record.note || record.productName}</strong>
                <small>{record.note ? `${record.productName} · ` : ''}历史测算记录 · {record.createdByName || '未知账号'} · 保存于 {savedAtText(record.savedAt)} · 点击载入</small>
              </span>
              <span className="jdc-result-price">{money(record.inputs.price)}</span>
            </button>
          ))}
          {results.length === 0 && recordMatches.length === 0 && <div className="jdc-empty">没有匹配的SKU或计算ID</div>}
          {results.map((variant) => (
            <button type="button" key={variant.variantId} className="jdc-result" onClick={() => selectVariant(variant)}>
              {variant.previewImage ? <img src={variant.previewImage} alt="" loading="lazy" /> : <span className="jdc-bar-noimg">无图</span>}
              <span className="jdc-result-name">
                <strong>{variant.productName}</strong>
                <small>{variant.variantAttributes || '默认规格'} · SKUID {variant.variantId} · 编码 {variant.merchantVariantCode || '—'}</small>
              </span>
              <span className="jdc-result-price">{variant.groupPrice !== null ? money(variant.groupPrice) : '—'}</span>
            </button>
          ))}
        </div>
      )}
    </>
  )

  return (
    <div className="page-stack pdd-page">
      <div className="page-title-row">
        <div>
          <span className="pdd-platform-label">PINDUODUO</span>
          <h1>利润计算器</h1>
          <p>选择SKU后按当前成本配置测算单件利润与保本ROI；所有参数可临时调整，仅本页测算，不会写回店铺成本配置。</p>
        </div>
        <div className="page-title-actions">
          {requestedVariantId && auth.canAccess(PDD_STORE_ID, 'pdd.sku') ? <Link className="text-link" to={`/pinduoduo/sku/${encodeURIComponent(requestedVariantId)}`}>返回SKU详情</Link> : null}
          {auth.canAccess(PDD_STORE_ID, 'pdd.costs') ? <Link className="text-link" to="/pinduoduo/costs">店铺成本配置</Link> : null}
          {catalog.data && <DataFreshnessBadge date={catalog.data.meta.exportedAt} label="商品主档" mode="on-demand" />}
        </div>
      </div>

      {!selected && (
        <section className="overview-section">
          <div className="section-heading-row">
            <div><h2>选择商品SKU</h2><p>搜索商品名称、规格、拼多多SKUID、SKU编码或商品ID；也可输入计算ID载入历史测算</p></div>
            <Search size={18} aria-hidden />
          </div>
          {searchPanel}
        </section>
      )}

      {selected && defaults && inputs && result && (
        <>
          <div className="jdc-bar">
            {selected.previewImage ? <img src={selected.previewImage} alt="" /> : <span className="jdc-bar-noimg">无图</span>}
            <div className="jdc-bar-main">
              <span className="jdc-bar-name" title={selected.productName}>{selected.productName}</span>
              <div className="jdc-bar-meta">
                <span className={`jdc-tag${defaults.isBrandSpecial ? ' is-special' : ' is-blue'}`}>
                  {defaults.isAccessory ? '配件' : defaults.isBrandSpecial ? '品牌特价' : '普通品'}
                </span>
                {defaults.storeCostEffectiveDate && <span className="jdc-tag">店铺成本版本 {defaults.storeCostEffectiveDate}</span>}
                <span>{selected.variantAttributes || '默认规格'}</span>
                <span>SKUID {selected.variantId}</span>
                <span>编码 {selected.merchantVariantCode || '—'}</span>
                <span>商品 {selected.productId}</span>
                {facts && (
                  <>
                    <span>总代价 <b>{facts.generalAgentPrice === null ? '—' : money(facts.generalAgentPrice)}</b></span>
                    <span>省代价 <b>{facts.provincialAgentPrice === null ? '—' : money(facts.provincialAgentPrice)}</b></span>
                    {facts.brandSpecialPrice !== null && <span>品牌特价 <b>{facts.brandSpecialConflict ? '冲突' : money(facts.brandSpecialPrice)}</b></span>}
                    <span>电商价 <b>{facts.ecommercePrice === null ? '—' : money(facts.ecommercePrice)}</b></span>
                    <span>ERP库存 <b>{facts.erpAvailable === null ? '—' : facts.erpAvailable.toLocaleString('zh-CN')}</b></span>
                  </>
                )}
                {!defaults.hasProductCost && <span className="jdc-tag is-warning">缺公司产品成本，产品成本默认0，请手动填写</span>}
                {defaults.costIssue && <span className="jdc-tag is-warning">公司成本冲突：{defaults.costIssue}</span>}
                {!defaults.hasStoreCost && <span className="jdc-tag is-warning">缺店铺成本版本，费用默认0</span>}
                {facts && !facts.hasCompanyRecord && <span className="jdc-tag is-warning">未关联公司产品编码</span>}
              </div>
            </div>
            <div className="jdc-bar-kpis">
              <div className="jdc-kpi is-hero"><span>含返点利润</span><strong className={result.profitWithRebate < 0 ? 'is-negative' : ''}>{money(result.profitWithRebate)}</strong></div>
              <div className="jdc-kpi"><span>利润率</span><strong>{percent(result.profitWithRebateRate)}</strong></div>
              <div className="jdc-kpi is-breakeven"><span>保本ROI</span><strong>{roi(result.breakEvenRoi)}</strong></div>
            </div>
            <div className="jdc-switch-wrap" ref={searchPopRef}>
              <button className="jdc-switch-btn" type="button" onClick={() => setSearchOpen((open) => !open)}>
                <ArrowLeftRight size={13} /> 切换商品
              </button>
              {searchOpen && <div className="jdc-pop">{searchPanel}</div>}
            </div>
          </div>

          <div className="jdc-layout">
            <div className="jdc-steps">
              <section className="jdc-step">
                <div className="jdc-step-head">
                  <span className="jdc-step-no">1</span>
                  <h2>成本参数</h2>
                  <span className="jdc-step-sub">默认取拼多多SKU与店铺当前成本版本，仅本页测算</span>
                  <button type="button" className="jdc-step-action" onClick={() => setInputs({ ...defaults.inputs })} disabled={!edited}>
                    <RotateCcw size={13} /> 重置为当前配置
                  </button>
                </div>
                <div className="jdc-step-body">
                  <div className="jdc-price-row">
                    <label className={`jdc-f${inputs.price !== defaults.inputs.price ? ' is-edited' : ''}`}>
                      <span className="jdc-f-label"><i className="jdc-dot" />订单成交价（扣点前）</span>
                      <span className="jdc-box"><input type="number" min={0} step="0.01" value={inputs.price} onChange={(event) => updateInput('price', event.target.value)} /><small>元</small></span>
                    </label>
                    <span className="jdc-note">默认拼单价；这是平台扣点的计费基数，不是扣点后的实际到账额</span>
                  </div>
                  <div className="jdc-groups">
                    <div className="jdc-group">
                      <div className="jdc-group-head">
                        <h3>履约成本</h3><span className="jdc-group-sub">每件发货即发生</span>
                        <span className="jdc-group-total">小计 <b>{money(result.fulfillmentCost)}</b></span>
                      </div>
                      <div className="jdc-fields">
                        {fulfillmentFields.map(({ key, label, unit }) => (
                          <label key={key} className={`jdc-f${inputs[key] !== defaults.inputs[key] ? ' is-edited' : ''}`}>
                            <span className="jdc-f-label"><i className="jdc-dot" />{label}</span>
                            <span className="jdc-box"><input type="number" min={0} step="0.01" value={inputs[key]} onChange={(event) => updateInput(key, event.target.value)} /><small>{unit}</small></span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="jdc-group">
                      <div className="jdc-group-head">
                        <h3>费率</h3><span className="jdc-group-sub">按售价 / 产品成本计</span>
                        <span className="jdc-group-total">扣点＋税点 <b>{money(result.rateFee)}</b> · 返点 <b>＋{money(result.rebateAmount)}</b></span>
                      </div>
                      <div className="jdc-fields">
                        {rateFields.map(({ key, label, unit, hint }) => (
                          <label key={key} className={`jdc-f${inputs[key] !== defaults.inputs[key] ? ' is-edited' : ''}`}>
                            <span className="jdc-f-label"><i className="jdc-dot" />{label}</span>
                            <span className="jdc-box"><input type="number" min={0} step="0.01" value={inputs[key]} onChange={(event) => updateInput(key, event.target.value)} /><small>{unit}</small></span>
                            {hint && <span className="jdc-note">{hint}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {afterSalesInputs && afterSalesResult && (
                <section className="jdc-step">
                  <div className="jdc-step-head">
                    <span className="jdc-step-no">2</span>
                    <h2>售后与广告</h2>
                    <span className="jdc-step-sub">按拼多多支付队列二退率，折算到每下单1件的期望值</span>
                    <button type="button" className="jdc-step-action" onClick={() => setAfterSalesInputs((previous) => afterSalesDefaultsFor(inputs, defaultSecondReturnRate(selected.productId) ?? previous?.secondReturnRatePercent ?? 0))}>
                      <RotateCcw size={13} /> 按当前参数重置
                    </button>
                  </div>
                  <div className="jdc-step-body">
                    <div className="jdc-dual">
                      <div className="jdc-group">
                        <div className="jdc-group-head">
                          <h3>退货售后</h3>
                          <span className="jdc-group-total">单件二退损失 <b>{money(afterSalesResult.unitSecondReturnLoss)}</b></span>
                        </div>
                        <p className="jdc-note">
                          实际二退率参考（支付队列口径，2026-05-09起）：全店 {secondReturnReference.storeRatePercent === null ? '—' : `${secondReturnReference.storeRatePercent.toFixed(1)}%`}
                          {secondReturnReference.productRatePercent(selected.productId) !== null
                            ? ` · 本商品 ${secondReturnReference.productRatePercent(selected.productId)!.toFixed(1)}%（下单 ${secondReturnReference.productOrderedItems(selected.productId)} 件）`
                            : secondReturnReference.issue ? ` · ${secondReturnReference.issue}；预计二退率请手动填写` : ' · 本商品下单不足20件，默认取全店比率'}
                        </p>
                        <div className="jdc-fields">
                          {afterSalesFields.map(({ key, label, unit, hint }) => (
                            <label key={key} className="jdc-f">
                              <span className="jdc-f-label">{label}</span>
                              <span className="jdc-box"><input type="number" min={0} max={key === 'secondReturnRatePercent' ? 100 : undefined} step="0.01" value={afterSalesInputs[key]} onChange={(event) => updateAfterSalesInput(key, event.target.value)} /><small>{unit}</small></span>
                              {hint && <span className="jdc-note">{hint}</span>}
                            </label>
                          ))}
                        </div>
                        <div className="jdc-inline-calc">损失＝往返运费＋包装损耗＋产品成本×货损率＝<b>{money(afterSalesResult.unitSecondReturnLoss)}</b></div>
                      </div>
                      <div className="jdc-group">
                        <div className="jdc-group-head">
                          <h3>广告投放</h3>
                          <span className="jdc-group-sub">自然单与多多推广单分开看</span>
                        </div>
                        <div className="jdc-fields">
                          <label className="jdc-f">
                            <span className="jdc-f-label">单件广告费（多多推广）</span>
                            <span className="jdc-box"><input type="number" min={0} step="0.01" value={adSpendText} placeholder="0.00" onChange={(event) => {
                              const raw = event.target.value
                              if (raw === '' || (Number.isFinite(Number(raw)) && Number(raw) >= 0)) setAdSpendText(raw)
                            }} /><small>元</small></span>
                            <span className="jdc-note">按每下单1件分摊；不与自然流量单重复扣费</span>
                          </label>
                        </div>
                        <div className="jdc-inline-calc">推广单利润＝考虑售后的期望利润－单件广告费 → 见右侧<b>渠道对比</b></div>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {cashRecovery && afterSalesInputs && (
                <div className="jdc-recovery-card jdc-recovery-wide">
                  <h3>回款估算 <span className="jdc-h-sub">不是平台结算账单</span></h3>
                  <div className="jdc-recovery-flow">
                    <div className="jdc-recovery-row is-formula">
                      <div className="jdc-recovery-formula-head">
                        <span><i>1</i><b>每清完1只预计变现</b><small>发生二退就继续卖，直到最终成功售出</small></span>
                        {hasCompleteRecoveryCosts && cashRecovery.expectedCashRecoveryPerClearedUnit !== null
                          ? <strong className={cashRecovery.expectedCashRecoveryPerClearedUnit < 0 ? 'is-negative' : ''}>{money(cashRecovery.expectedCashRecoveryPerClearedUnit)}</strong>
                          : <strong>—</strong>}
                      </div>
                      <div className="jdc-recovery-formula">
                        <span className="jdc-formula-term is-income"><b>{money(cashRecovery.clearedUnitBreakdown.price)}</b><small>成交价</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.platformFee)}</b><small>平台扣点<br />{money(inputs.price)}×{inputs.platformFeeRatePercent}%</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.taxFee)}</b><small>税费<br />{money(inputs.price)}×{inputs.taxRatePercent}%</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.packagingCost)}</b><small>包装费</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.giftCost)}</b><small>赠品</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.shippingInsuranceCost)}</b><small>运费险</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.initialShippingCost)}</b><small>首次发货运费</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.expectedAdvertisingCost)}</b><small>清完预计广告费<br />{money(adSpendForRecovery)}÷{100 - afterSalesInputs.secondReturnRatePercent}%</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.expectedRetryCashCost)}</b><small>二退重试支出<br />({money(afterSalesInputs.returnShippingCost)}＋{money(afterSalesInputs.packagingLossCost)})×{afterSalesInputs.secondReturnRatePercent}%÷{100 - afterSalesInputs.secondReturnRatePercent}%</small></span>
                        <em>＝</em><span className="jdc-formula-term is-result"><b>{cashRecovery.expectedCashRecoveryPerClearedUnit === null ? '—' : money(cashRecovery.expectedCashRecoveryPerClearedUnit)}</b><small>每清完1只预计变现</small></span>
                      </div>
                      <p>产品成本 {money(inputs.productCost)} 是已经投入的库存成本，不在本次现金变现中重复扣除。</p>
                    </div>
                    <div className="jdc-recovery-row is-final">
                      <span><i>2</i><b>全部清完预计总变现</b><small>{inventoryQuantity === null ? '缺ERP可用库存' : `ERP可用库存 ${inventoryQuantity.toLocaleString('zh-CN')} 件，退回后继续卖`}</small></span>
                      {hasCompleteRecoveryCosts && cashRecovery.estimatedInventoryCashRecovery !== null
                        ? <strong className={cashRecovery.estimatedInventoryCashRecovery < 0 ? 'is-negative' : ''}>{money(cashRecovery.estimatedInventoryCashRecovery)}</strong>
                        : <strong>—</strong>}
                    </div>
                  </div>
                  <p className="jdc-footnote">
                    净变现表示这批库存还能换回多少现金，不扣已经投入的产品成本 {money(inputs.productCost)}；利润或亏损仍看右侧“单件测算”。售后期望按二退率 {afterSalesInputs.secondReturnRatePercent}% 计算；总变现假设退回库存继续销售，直到全部成功卖出。
                    {adSpendText === '' ? ' 当前未填写广告费，暂按0元计算。' : ''}
                    {!hasCompleteRecoveryCosts ? ' 当前店铺成本不完整，变现金额暂不展示。' : ''}
                  </p>
                </div>
              )}
            </div>

            <aside className="jdc-rail">
              <div className="jdc-card">
                <h3>利润解剖 <span className="jdc-h-sub">售价 {money(inputs.price)} 拆解</span></h3>
                {inputs.price > 0 ? (
                  <>
                    <div className="jdc-anatomy-bar">
                      <div className="seg jdc-seg-cost" style={{ width: `${Math.max(0, Math.min(100, inputs.productCost / inputs.price * 100)).toFixed(1)}%` }} />
                      <div className="seg jdc-seg-fulfill" style={{ width: `${Math.max(0, Math.min(100, (result.fulfillmentCost - inputs.productCost) / inputs.price * 100)).toFixed(1)}%` }} />
                      <div className="seg jdc-seg-fee" style={{ width: `${Math.max(0, Math.min(100, result.rateFee / inputs.price * 100)).toFixed(1)}%` }} />
                      <div className={`seg ${result.profit >= 0 ? 'jdc-seg-profit' : 'jdc-seg-loss'}`} style={{ width: `${Math.max(0, Math.min(100, Math.abs(result.profit) / inputs.price * 100)).toFixed(1)}%` }} />
                    </div>
                    <div className="jdc-legend">
                      <div className="lg"><i className="jdc-seg-cost" />产品成本<b>{money(inputs.productCost)}</b></div>
                      <div className="lg"><i className="jdc-seg-fulfill" />包装·赠品·运费·运费险<b>{money(result.fulfillmentCost - inputs.productCost)}</b></div>
                      <div className="lg"><i className="jdc-seg-fee" />平台扣点＋税点<b>{money(result.rateFee)}</b></div>
                      <div className="lg"><i className={result.profit >= 0 ? 'jdc-seg-profit' : 'jdc-seg-loss'} />{result.profit >= 0 ? '利润（不含返点）' : '亏损（不含返点）'}<b className={result.profit < 0 ? 'is-negative' : ''}>{money(result.profit)}</b></div>
                      <div className="lg"><small>返点另计：产品成本×{inputs.rebateRatePercent}%＝</small><b style={{ color: 'var(--positive)' }}>＋{money(result.rebateAmount)}</b></div>
                    </div>
                  </>
                ) : <div className="jdc-empty">售价为0，请先在左侧填写订单成交价</div>}
              </div>

              <div className="jdc-card">
                <h3>单件测算 <span className="jdc-h-sub">口径与拼多多经营页一致</span></h3>
                <div className="jdc-hero">
                  <div className="jdc-hm"><span>含返点利润</span><strong className={result.profitWithRebate < 0 ? 'is-negative' : ''}>{money(result.profitWithRebate)}</strong></div>
                  <div className="jdc-hm hm-sub"><span>利润率</span><b>{percent(result.profitWithRebateRate)}</b></div>
                  <div className="jdc-hm hm-sub"><span>保本ROI</span><b className="jdc-be">{roi(result.breakEvenRoi)}</b></div>
                </div>
                <div className="jdc-rows">
                  <div className="rr"><span>履约成本</span><b>{money(result.fulfillmentCost)}</b></div>
                  <div className="rr"><span>扣点与税点</span><b>{money(result.rateFee)}</b></div>
                  <div className="rr"><span>预估利润（不含返点）</span><b className={result.profit < 0 ? 'is-negative' : ''}>{money(result.profit)}</b></div>
                  <div className="rr"><span>返点（成本×{inputs.rebateRatePercent}%）</span><b>＋{money(result.rebateAmount)}</b></div>
                </div>
              </div>

              {afterSalesInputs && afterSalesResult && (
                <div className="jdc-card">
                  <h3>考虑退货售后 <span className="jdc-h-sub">二退率 {afterSalesInputs.secondReturnRatePercent}%</span></h3>
                  <div className="jdc-hero">
                    <div className="jdc-hm"><span>计入二退后的单件利润</span><strong style={{ fontSize: 23 }} className={afterSalesResult.expectedProfitPerOrdered < 0 ? 'is-negative' : ''}>{money(afterSalesResult.expectedProfitPerOrdered)}</strong></div>
                    <div className="jdc-hm hm-sub"><span>售后保本ROI</span><b className="jdc-be">{roi(afterSalesResult.afterSalesBreakEvenRoi)}</b></div>
                  </div>
                  <div className="jdc-rows">
                    <div className="rr"><span>单件二退损失</span><b>{money(afterSalesResult.unitSecondReturnLoss)}</b></div>
                    <div className="rr"><span>平均售后成本</span><b>{money(afterSalesResult.expectedLossPerOrdered)}</b></div>
                  </div>
                </div>
              )}

              <div className="jdc-card">
                <h3>广告渠道对比 <span className="jdc-h-sub">分渠道看每下单件利润</span></h3>
                <div className="jdc-channels">
                  <div className="jdc-channel">
                    <span className="ch-name">自然流量单</span>
                    <span className="ch-note">不分摊广告费</span>
                    <div className="ch-profit"><b className={afterSalesResult && afterSalesResult.expectedProfitPerOrdered < 0 ? 'is-negative' : ''}>{afterSalesResult ? money(afterSalesResult.expectedProfitPerOrdered) : '—'}</b><span>单件利润</span></div>
                  </div>
                  <div className="jdc-channel">
                    <span className="ch-name">推广单 · 多多推广</span>
                    <span className="ch-note">已扣单件广告费 {adSpendText === '' ? '—' : money(adSpend)}</span>
                    <div className="ch-profit"><b className={adSimulation && adSimulation.profitAfterAdvertising < 0 ? 'is-negative' : ''}>{adSimulation ? money(adSimulation.profitAfterAdvertising) : '—'}</b><span>单件利润</span></div>
                  </div>
                </div>
              </div>

              <div className="jdc-card jdc-save">
                <label className="jdc-record-note-field">
                  <span>备注名称 <small>可选，用于区分不同测算方案</small></span>
                  <input
                    type="text"
                    value={recordNoteText}
                    maxLength={80}
                    placeholder="例如：低价清仓 / 多多推广保本方案"
                    onChange={(event) => {
                      setRecordNoteText(event.target.value)
                      setSaveMessage('')
                    }}
                  />
                </label>
                {hasLoadedEditableRecord && (
                  <button type="button" className="primary-button" onClick={() => void saveCurrent('overwrite')} disabled={recordsBusy}>
                    <Save size={14} /> {recordsBusy ? '保存中…' : hasLoadedOwnRecord ? '保存（覆盖原记录）' : '保存共享记录'}
                  </button>
                )}
                <button type="button" className={hasLoadedRecord ? 'jdc-btn-ghost' : 'primary-button'} onClick={() => void saveCurrent('new')} disabled={recordsBusy || Boolean(hasLoadedRecord && !loadedRecord?.canCopy)}>
                  {hasLoadedOwnRecord
                    ? <><SaveAll size={14} /> 另存为新记录</>
                    : hasLoadedRecord
                      ? <><SaveAll size={14} /> 另存为我的记录</>
                    : <><Save size={14} /> {recordsBusy ? '保存中…' : '保存本次测算'}</>}
                </button>
                {hasLoadedOwnRecord && loadedRecord && <button type="button" className="jdc-btn-ghost jdc-share-button" onClick={() => setShareRecord(loadedRecord)}><Share2 size={14} />分享{loadedRecord.shareCount ? `（${loadedRecord.shareCount}人）` : ''}</button>}
                {conflictRevision !== null && <div className="jdc-conflict">这条测算已被别人更新到版本 {conflictRevision}，本次没有覆盖。<button type="button" onClick={() => { void refreshRecords().then((next) => { const latest = next.find((item) => item.id === loadedRecordId); if (latest) loadRecord(latest) }) }}>载入最新版本</button></div>}
                {saveMessage && <div className="form-success">{saveMessage}</div>}
                <p className="jdc-footnote">测算为单件静态估算，未包含活动让利和实际结算差异；真实经营利润以店铺总览与SKU详情为准。</p>
              </div>

            </aside>
          </div>
        </>
      )}

      <section className="overview-section jdc-history">
        <div className="section-heading-row">
          <div><h2>历史测算记录</h2><p>{recordScope === 'mine' ? '默认只显示当前账号保存的测算；保存后可定向分享。' : '这里只显示别人明确分享给你的测算。'}</p></div>
          <History size={18} aria-hidden />
        </div>
        <div className="jdc-history-tabs" role="tablist" aria-label="历史测算记录范围">
          <button type="button" role="tab" aria-selected={recordScope === 'mine'} className={recordScope === 'mine' ? 'is-active' : ''} onClick={() => setRecordScope('mine')}>我的测算 <span>{ownRecords.length}</span></button>
          <button type="button" role="tab" aria-selected={recordScope === 'shared'} className={recordScope === 'shared' ? 'is-active' : ''} onClick={() => setRecordScope('shared')}>分享给我的 <span>{sharedRecords.length}</span></button>
        </div>
        {recordsError && <div className="form-error">{recordsError}</div>}
        {visibleRecords.length === 0 && !recordsError && <div className="jdc-empty">{recordScope === 'mine' ? '当前账号还没有保存过测算记录；测算后点击「保存本次测算」。' : '还没有人向你分享测算记录。'}</div>}
        {visibleRecords.length > 0 && (
          <div className="table-responsive">
            <table>
              <thead><tr><th>计算ID</th><th>备注名称</th><th>更新时间</th><th>所有者</th><th>权限</th><th>最后编辑</th><th>商品</th><th>售价</th><th>含返点利润</th><th style={{ textAlign: 'right' }}>操作</th></tr></thead>
              <tbody>
                {visibleRecords.map((record) => {
                  const recordResult = computePinduoduoUnitProfit(record.inputs)
                  const recordImage = record.previewImage
                    || catalog.data?.variants.find((item) => item.variantId === record.variantId)?.previewImage
                    || ''
                  return (
                    <tr key={record.id}>
                      <td>
                        {record.code
                          ? <span className="jdc-code" role="button" title="点击复制计算ID" onClick={() => copyRecordCode(record.code!)}>{copiedCode === record.code ? '已复制' : record.code}</span>
                          : '—'}
                      </td>
                      <td className="jdc-h-note" title={record.note || ''}>{record.note || '—'}</td>
                      <td className="jdc-h-time">{record.updatedAt ? new Date(record.updatedAt * 1000).toLocaleString('zh-CN', { hour12: false }).slice(0, 16) : savedAtText(record.savedAt)}</td>
                      <td><span className="jdc-owner-badge">{record.createdByName || '未知账号'}</span></td>
                      <td><span className={`calculator-access-badge is-${record.accessLevel || (record.isMine ? 'owner' : 'read')}`}>{record.isMine ? '所有者' : record.accessLevel === 'edit' ? '可编辑' : '只读'}</span></td>
                      <td><span className="jdc-owner-badge">{record.lastEditedByName || record.createdByName || '未知账号'}</span></td>
                      <td>
                        <div className="jdc-h-product">
                          {recordImage ? <img src={recordImage} alt="" loading="lazy" /> : <span className="jdc-bar-noimg">无图</span>}
                          <div className="jdc-h-name">
                            <strong title={record.productName}>{record.productName}</strong>
                            <small>{record.variantAttributes || '默认规格'} · SKUID {record.variantId}{record.merchantVariantCode ? ` · 编码 ${record.merchantVariantCode}` : ''}</small>
                          </div>
                        </div>
                      </td>
                      <td>{money(record.inputs.price)}</td>
                      <td style={recordResult.profitWithRebate < 0 ? { color: 'var(--negative)', fontWeight: 650 } : { color: 'var(--positive)', fontWeight: 650 }}>{money(recordResult.profitWithRebate)}</td>
                      <td className="jdc-h-actions">
                        <button type="button" onClick={() => loadRecord(record)}>{record.id === loadedRecordId ? '已载入' : record.canEdit ? '载入编辑' : '载入查看'}</button>
                        {!record.isMine && <Link to={`/shared-calculations/pinduoduo/${encodeURIComponent(record.id)}`}>查看分享页</Link>}
                        {record.canShare && <button type="button" className="jdc-share-button" onClick={() => setShareRecord(record)}><Share2 size={12} /> 分享</button>}
                        {record.canDelete && <button type="button" className="is-danger" onClick={() => deleteRecord(record.id)} disabled={recordsBusy}><Trash2 size={12} /> 删除</button>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {shareRecord && <CalculatorShareDialog platform="pinduoduo" record={shareRecord} onClose={() => setShareRecord(null)} onSaved={() => { void refreshRecords() }} />}
    </div>
  )
}
